import API_BASE from './config';
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ParticipantLogin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('login'); // 'login' | 'register'

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Register state
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');

  // Forgot Password state
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      const res = await axios.post(`${cleanBase}/api/participant/login/`, {
        email: loginEmail.toLowerCase(),
        password: loginPassword,
      });
      localStorage.setItem('participant_access_token', res.data.access);
      localStorage.setItem('participant_refresh_token', res.data.refresh);
      localStorage.setItem('participant_user', JSON.stringify(res.data.user));
      // Redirect to My Tickets or homepage
      const redirect = new URLSearchParams(window.location.search).get('next') || '/';
      navigate(redirect);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Login failed. Check your credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetLoading(true);
    try {
      const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      await axios.post(`${cleanBase}/api/password_reset/`, {
        email: resetEmail.toLowerCase(),
      });
      setResetMessage('Check your email for the reset link!');
    } catch (err) {
      setResetMessage(err.response?.data?.email?.[0] || 'User with this email not found.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (regPassword !== regConfirm) {
      setRegError('Passwords do not match.');
      return;
    }
    if (regPassword.length < 8) {
      setRegError('Password must be at least 8 characters.');
      return;
    }
    setRegLoading(true);
    try {
      const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      const res = await axios.post(`${cleanBase}/api/participant/register/`, {
        first_name: regFirstName,
        last_name: regLastName,
        email: regEmail.toLowerCase(),
        phone: regPhone,
        password: regPassword,
      });
      localStorage.setItem('participant_access_token', res.data.access);
      localStorage.setItem('participant_refresh_token', res.data.refresh);
      localStorage.setItem('participant_user', JSON.stringify(res.data.user));
      navigate('/my-tickets');
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.email?.[0] || data?.error || data?.password?.[0] || 'Registration failed.';
      setRegError(msg);
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background glows */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[50%] bg-sky-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[50%] bg-sky-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-10 cursor-pointer" onClick={() => navigate('/')}>
          <img
            src="/logo192.png"
            alt="EventDeck"
            className="w-10 h-10 object-contain"
          />
          <h1 className="text-xl font-black tracking-tighter uppercase text-white">EVENT<span className="text-sky-500">DECK</span></h1>
        </div>

        {/* Card */}
        <div className="bg-white/[0.02] border border-white/[0.07] rounded-[2rem] overflow-hidden shadow-2xl">
          {/* Tab switcher */}
          <div className="grid grid-cols-2 border-b border-white/[0.06]">
            <button
              onClick={() => { setTab('login'); setForgotMode(false); setLoginError(''); setRegError(''); }}
              className={`py-4 text-[10px] font-black uppercase tracking-widest transition-all ${tab === 'login' ? 'bg-sky-500 text-black' : 'text-gray-500 hover:text-white'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setTab('register'); setForgotMode(false); setLoginError(''); setRegError(''); }}
              className={`py-4 text-[10px] font-black uppercase tracking-widest transition-all ${tab === 'register' ? 'bg-sky-500 text-black' : 'text-gray-500 hover:text-white'}`}
            >
              Create Account
            </button>
          </div>

          <div className="p-8">
            {/* ── LOGIN ── */}
            {tab === 'login' && (
              forgotMode ? (
                /* ── FORGOT PASSWORD VIEW ── */
                <form onSubmit={handleRequestReset} className="space-y-5 animate-in fade-in duration-300">
                  <div>
                    <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-1">Security</p>
                    <h2 className="text-2xl font-black text-white uppercase tracking-tight">Reset Password</h2>
                  </div>

                  {resetMessage && (
                    <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl px-4 py-3">
                      <p className="text-sky-400 text-xs font-bold">{resetMessage}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Registered Email</label>
                    <input
                      type="email" required value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                    />
                  </div>

                  <button
                    type="submit" disabled={resetLoading}
                    className="w-full bg-sky-500 hover:bg-sky-400 text-black font-black py-4 rounded-2xl uppercase text-[11px] tracking-[0.2em] transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-sky-500/20"
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link →'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setForgotMode(false)}
                    className="w-full text-center text-[10px] text-gray-600 font-bold uppercase hover:text-white transition-colors"
                  >
                    ← Back to Sign In
                  </button>
                </form>
              ) : (
                /* ── STANDARD LOGIN FORM ── */
                <form onSubmit={handleLogin} className="space-y-5 animate-in fade-in duration-300">
                  <div>
                    <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-1">Welcome Back</p>
                    <h2 className="text-2xl font-black text-white uppercase tracking-tight">Sign In</h2>
                  </div>

                  {loginError && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                      <p className="text-red-400 text-xs font-bold">{loginError}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Email</label>
                      <input
                        type="email" required value={loginEmail}
                        onChange={e => setLoginEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Password</label>
                      <input
                        type="password" required value={loginPassword}
                        onChange={e => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                      />
                      <div className="flex justify-end mt-1">
                        <button
                          type="button"
                          onClick={() => { setForgotMode(true); setResetMessage(''); }}
                          className="text-[10px] text-sky-500 font-bold uppercase hover:text-sky-400 transition-colors"
                        >
                          Forgot Password?
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit" disabled={loginLoading}
                    className="w-full bg-sky-500 hover:bg-sky-400 text-black font-black py-4 rounded-2xl uppercase text-[11px] tracking-[0.2em] transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-sky-500/20"
                  >
                    {loginLoading ? 'Signing In...' : 'Sign In →'}
                  </button>

                  <p className="text-center text-[10px] text-gray-600">
                    No account?{' '}
                    <button type="button" onClick={() => setTab('register')} className="text-sky-500 font-black hover:text-sky-400 transition-colors">
                      Create one free
                    </button>
                  </p>
                </form>
              )
            )}

            {/* ── REGISTER ── */}
            {tab === 'register' && (
              <form onSubmit={handleRegister} className="space-y-5 animate-in fade-in duration-300">
                <div>
                  <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-1">New Account</p>
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight">Join EventDeck</h2>
                </div>

                {regError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                    <p className="text-red-400 text-xs font-bold">{regError}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">First Name *</label>
                    <input
                      type="text" required value={regFirstName}
                      onChange={e => setRegFirstName(e.target.value)}
                      placeholder="First"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Last Name</label>
                    <input
                      type="text" value={regLastName}
                      onChange={e => setRegLastName(e.target.value)}
                      placeholder="Last"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Email *</label>
                  <input
                    type="email" required value={regEmail}
                    onChange={e => setRegEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Phone</label>
                  <input
                    type="tel" value={regPhone}
                    onChange={e => setRegPhone(e.target.value)}
                    placeholder="01XXXXXXXXX"
                    className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Password *</label>
                    <input
                      type="password" required value={regPassword}
                      onChange={e => setRegPassword(e.target.value)}
                      placeholder="Min 8 chars"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Confirm *</label>
                    <input
                      type="password" required value={regConfirm}
                      onChange={e => setRegConfirm(e.target.value)}
                      placeholder="Repeat"
                      className={`w-full bg-black/40 border rounded-2xl px-4 py-3.5 text-white outline-none transition-all text-sm ${regConfirm && regPassword !== regConfirm ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-sky-500'}`}
                    />
                  </div>
                </div>

                <button
                  type="submit" disabled={regLoading}
                  className="w-full bg-sky-500 hover:bg-sky-400 text-black font-black py-4 rounded-2xl uppercase text-[11px] tracking-[0.2em] transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-sky-500/20"
                >
                  {regLoading ? 'Creating Account...' : 'Create Account →'}
                </button>

                <p className="text-center text-[10px] text-gray-600">
                  Already have an account?{' '}
                  <button type="button" onClick={() => setTab('login')} className="text-sky-500 font-black hover:text-sky-400 transition-colors">
                    Sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Back link */}
        <p className="text-center mt-6">
          <button onClick={() => navigate('/')} className="text-[10px] text-gray-700 hover:text-gray-400 uppercase tracking-widest font-bold transition-colors">
            ← Back to Events
          </button>
        </p>
      </div>
    </div>
  );
};

export default ParticipantLogin;